import { useEffect, useState } from "react";

import {
  DatePicker,
  TextField,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DEFAULT_REQUEST } from "./constants";
import {
  getRequest,
  getRequestDocument,
  getForm6,
  saveForm6,
} from "../../api/Request/RequestApi";
import { getSchoolYear } from "../../api/SchoolYear/SchoolYearApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role, SchoolYear } from "../../models";
import {
  Request,
  RequestStatus,
  DocumentType,
  Form6,
} from "../../models/Request";
import { Attachment } from "../../models/Shared/Attachment";
import { requestAdminDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function Form6Edit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefaultForm6 = (): Form6 => {
    return {
      id: 0,
      requestId: +id,
      sessionDate: new Date(),
    };
  };
  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [form6, setForm6] = useState<Form6>(createDefaultForm6());
  const [schoolYear, setSchoolYear] = useState<SchoolYear>();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();

  const isFormValid = !!(
    form6.administrativeDepartmentName &&
    form6.sessionPlace &&
    form6.sessionDate != new Date());

  const saveButtonVisibility = (() => {
    if(!isEditing)
      return "hide";
    else if( isFormValid )
      return "show";
    else
      return "disable";
  })

  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        const result = await getForm6(+id);
        if (result) {
          setForm6(result);
          setIsEditing(false);
          if (result.request) {
            setItem(result.request);
          }
        } else {
          const request = await getRequest(+id);
          if (request) {
            setItem(() => request); // Can we force this to be synchronous?
          }
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    (async () => {
      if (item && item.schoolYearId && item.schoolYearId > 0) {
        const schoolYear = await getSchoolYear(item.schoolYearId);
        setSchoolYear(schoolYear);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.schoolYearId]);

  const handleEditingClick = () => {
    setIsEditing(true);
  };

  const handleFormChange = <K extends keyof Form6>(key: K, val?: Form6[K]) => {
    const d: Form6 = {
      ...form6,
      [key]: typeof val === "string" ? (val.length > 0 ? val : undefined) : val,
    };
    handleUpdate(d);
  };

  const handleDocumentDownload = async (doc: Attachment) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, DocumentType.Form6);
    }
  };
  const handleUpdate = (item: Form6) => {
    setForm6(item);
    dispatch(setIsEdited());
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={
          !isEditing &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            ((auth.currentRole?.role === Role.CountyLeader ||
              auth.currentRole?.role === Role.CountyAdministrator) &&
              auth.currentRole?.locations?.find(
                (v) => v === (item.parentCountyId ?? item.countyId)
              ) !== undefined)) &&
          ![
            RequestStatus.Cancelled,
            RequestStatus.Registered,
            RequestStatus.DecisionSuspended,
          ].includes(item.statusId) &&
          (schoolYear?.isCurrent ?? false)
            ? "show"
            : "hide"
        }
        pageTitle={t("form6.pageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("form6.data")}>
        <Row>
          <Col md={12}>
            <DatePicker
              label={t("form6.sessionDate")}
              name={"form6.sessionDate"}
              onChange={(e) =>
                handleFormChange("sessionDate", new Date(e.target.value))
              }
              value={form6.sessionDate ? new Date(form6.sessionDate) : null}
              minYear={new Date().getFullYear()}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionDate"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("form6.administrativeDepartmentName")}
              name={nameof<typeof form6>("administrativeDepartmentName")}
              value={form6.administrativeDepartmentName}
              onChange={(e) =>
                handleFormChange("administrativeDepartmentName", e.target.value)
              }
              plaintext={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("AdministrativeDepartmentName")
                )?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("form6.sessionPlace")}
              name={nameof<typeof form6>("sessionPlace")}
              value={form6.sessionPlace}
              onChange={(e) => handleFormChange("sessionPlace", e.target.value)}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionPlace"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("form6.classification")}
              name={nameof<typeof form6>("classification")}
              value={form6.classification}
              onChange={(e) =>
                handleFormChange("classification", e.target.value)
              }
              maxLength={50}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("Classification")
                )?.message
              }
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("form6.associationNumber")}
              name={nameof<typeof form6>("associationNumber")}
              value={form6.associationNumber}
              onChange={(e) =>
                handleFormChange("associationNumber", e.target.value)
              }
              maxLength={50}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("AssociationNumber")
                )?.message
              }
            />
          </Col>
        </Row>
        {!isEditing && form6.attachment && (
          <DocumentList
            documents={form6.attachment ? [form6.attachment] : []}
            onDocumentClick={(doc) => handleDocumentDownload(doc)}
            removable={false}
          />
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={saveButtonVisibility()}
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        handleSave={async () => saveForm6(form6)}
        setSaveErrors={setErrors}
        showCustom={false}
      />
    </ContentArea>
  );
}
